// composables/useCommon.js
import {computed, getCurrentInstance} from 'vue';
import {callApi as apiCall} from '@/api';
import LogService from '@/services/LogService';
import {camelCase} from 'lodash';
import {addAmPm, getLanguageFromLanguageParam} from '@/common/common';
import configEnv from "@/enums/configEnv";

export function useCommon() {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error('useCommon must be called within setup()');
  }

  const { proxy } = instance;

  // computed properties
  const lang = computed(() => proxy.$store.state.lang);
  const phoneCodes = computed(() => [
    { code: '49', text: '+49', country: 'Deutschland', flag: '' },
  ]);
  const routeLang = computed(() => proxy.$route.params?.lang || '');

  // methods
  async function getWordPressStrings(str, sectionArray, pageTitleSection = undefined, selector = 'title', l = undefined) {
    LogService.log('calling wp strings');
    await proxy.$wpAPI.getStringsArray(
      sectionArray,
      l ?? routeLang.value
    ).then((results) => {
      LogService.log('wp strings returned', results);
      str.value = Object.freeze({
        ...str.value,
        ...results,
      });
      if (pageTitleSection !== undefined) {
        const pageTitle = str.value[camelCase(pageTitleSection)][selector];
        proxy.$store.commit('SET_PAGE_TITLE', pageTitle);
        LogService.log('getWordPressStrings, set pageTitle', pageTitle);
      }
    });
  }

  async function saveBeforeLeave() {
    const infoApi = '/api/order/';
    await callApi({ url: infoApi, method: 'post' }, proxy.$store);
  }

  function callApi(o) {
    LogService.log('callApi', o);
    return apiCall(o, proxy.$store);
  }

  function checkIfRedirectToEnglishNecessary() {
    const isBot = () => {
      if (typeof window === 'undefined') return false;
      const userAgent = navigator.userAgent.toLowerCase();
      const bots = [
        'bot',
        'googlebot',
        'crawler',
        'spider',
        'robot',
        'bingbot',
        'google-inspectiontool',
        'lighthouse',
        'facebookexternalhit',
        'twitterbot'
      ];
      return new RegExp(bots.join('|'), 'i').test(userAgent);
    };
    if (isBot()) return false;

    const { locale } = Intl.NumberFormat().resolvedOptions();
    const supportedLocales = navigator.languages;
    const { lang: routeLangParam } = proxy.$route.params;
    const { just_arrived } = proxy.$route.query;
    const deSupported = supportedLocales.some((x) => x.startsWith('de'));

    if (just_arrived && locale?.startsWith('en') && !deSupported && routeLangParam === 'de') {
      const { query } = proxy.$route;
      if ('just_arrived' in query) delete query.just_arrived;
      proxy.$router.replace({ name: proxy.$route.name, params: { ...proxy.$route.params, lang: 'en' }, query });
      return true;
    }
    return false;
  }

  function format(template, ...args) {
    let n = -1;
    return template?.replace(/(%s)/g, () => {
      n += 1;
      return args[n];
    });
  }

  function formatHourAmPm(hour, paramLang) {
    const l = getLanguageFromLanguageParam(paramLang);
    return l === 'en' ? addAmPm(hour) : String(hour);
  }

  function formatAdjacentHours(str, start, end, paramLang) {
    const l = getLanguageFromLanguageParam(paramLang);
    if (l === 'en') {
      return format(str.value.orderInfo?.between_hours_adjacent, addAmPm(start), addAmPm(end));
    }
    return format(str.value.orderInfo?.between_hours_adjacent, start, end);
  }

  function getDiffInDays(date1, date2) {
    const oneDay = 24 * 60 * 60 * 1000;
    const diffInTime = Math.abs(date2.getTime() - date1.getTime());
    return Math.ceil(diffInTime / oneDay);
  }

  function getValidatedServerType() {
    const serverType = configEnv?.server_type;
    return serverType.toLowerCase(); // z.B. "wetterheld" für Ordnerpfade
  }

  async function getPageStrings(str, sectionArray, titleSection = undefined, externalLang = undefined) {
    LogService.log('calling json strings');

    // Server-Type automatisch ermitteln & prüfen
    const serverType = getValidatedServerType();
    let targetLang = externalLang ?? routeLang.value;
    if (targetLang === '') {
      targetLang = configEnv.default_wp_language;
    }
    LogService.log("lang", targetLang);

    // Parallele Promises für alle Sections aufbauen
    const tasks = sectionArray.map(async (section) => {
      // Falls bestimmte Sections wie beim Mixin nur 'all' nutzen (optional beibehalten falls benötigt)
      const languageToUse = configEnv.one_version_json_content_files?.includes(section)
        ? 'all'
        : targetLang;

      // Eindeutiger Cache-Schlüssel pro Section + Sprache
      const cacheKey = `${serverType}_${section}_${languageToUse}`;

      // A. Prüfen, ob die Section bereits im Store-Vault liegt
      let sectionData = proxy.$store.state.contentCache?.[cacheKey];

      if (!sectionData) {
        LogService.log(`Fetching section "${section}" (${languageToUse}) from JSON file...`);

        try {
          // Schritt 1: API-Call zum Abrufen der konkreten Section-JSON-Datei
          const url = `${configEnv.content_path}${section}/${languageToUse}.json`;
          LogService.log(`Fetching section "${section}" (${languageToUse}) from URL: ${url}`);
          const response = await proxy.$axios.get(url);
          sectionData = response.data;

          // B. Im Store ablegen
          proxy.$store.commit('SET_CONTENT_CACHE', {
            key: cacheKey,
            data: sectionData
          });
        } catch (error) {
          LogService.log(`Error loading section "${section}" (${languageToUse})`, error);
          sectionData = {};
        }
      } else {
        LogService.log(`Serving section "${section}" (${languageToUse}) from Store Vault!`);
      }

      proxy.$store.commit('SET_STRINGS', {
        group: camelCase(section),
        strings: sectionData,
      });

      return {
        key: camelCase(section),
        data: sectionData
      };
    });

    // Alle HTTP-Anfragen parallel ausführen
    const results = await Promise.all(tasks);

    // Ergebnisse in ein einzelnes Objekt transformieren
    const combinedResults = {};
    results.forEach(({ key, data }) => {
      combinedResults[key] = data;
    });

    // Seitentitel im Store setzen (sofern gefordert und vorhanden)
    if (titleSection) {
      const pageTitle = combinedResults[camelCase(titleSection)]?.['title'];
      if (pageTitle) {
        proxy.$store.commit('SET_PAGE_TITLE', pageTitle);
        LogService.log('getPageStrings, set pageTitle', pageTitle);
      }
    }

    // Gesamtergebnis im Ref setzen (Sicheres Entkoppeln bestehender eingefrorener Daten)
    const currentStr = str.value ? { ...str.value } : {};
    str.value = Object.freeze({
      ...currentStr,
      ...combinedResults,
    });
  }

  return {
    lang,
    phoneCodes,
    routeLang,
    getWordPressStrings,
    saveBeforeLeave,
    callApi,
    checkIfRedirectToEnglishNecessary,
    format,
    formatHourAmPm,
    formatAdjacentHours,
    getDiffInDays,
    getPageStrings,
  };
}
